import styles from './HillCaptain.module.css';

import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import WbSunny from '@mui/icons-material/WbSunny';
import Assignment from '@mui/icons-material/Assignment';
import Air from '@mui/icons-material/Air';
import DownhillSkiing from '@mui/icons-material/DownhillSkiing';
import StickyNote2 from '@mui/icons-material/StickyNote2';
import Sync from '@mui/icons-material/Sync';
import Notifications from '@mui/icons-material/Notifications';

import * as api from '../../api.js';
import { useAuth } from '../../AuthContext';
import { useRealtimeTrigger } from '../../hooks/useRealtimeTrigger';

import { ShiftReport } from './ShiftReport';
import { EquipmentCheckout } from './EquipmentCheckout';
import { OxygenTracking } from './OxygenTracking';
import { TobogganStatus } from './TobogganStatus';
import { Memos } from './Memos';
import { Alerts } from './Alerts';
import { CollapsibleCard } from '../CollapsibleCard/CollapsibleCard';

export const HillCaptain = ({ user, currentShift }) => {
    const [ error, setError ] = React.useState(null);
    const [ activeTab, setActiveTab ] = React.useState(1);
    const [ isLoading, setIsLoading ] = React.useState(false);
    const [ activeAlerts, setActiveAlerts ] = React.useState(false);

    // Data states for each component
    const [ reportData, setReportData ] = React.useState(null);
    const [ equipmentData, setEquipmentData ] = React.useState([]);
    const [ oxygenData, setOxygenData ] = React.useState([]);
    const [ tobogganData, setTobogganData ] = React.useState([]);
    const [ memoData, setMemoData ] = React.useState([]);

    const { token } = useAuth();

    // Check if user has Hill Captain certification
    const hasHillCaptainCertification = user?.certifications?.some(cert => {
        return cert.name === 'Hill Captain' && cert.patrollers?.includes(user.id);
    });

    // Check if user is assigned to a Hill Captain role in the current shift
    const hillCaptainRole = currentShift?.roles?.find(role => role.name === 'Hill Captain');
    const isAssignedAsHillCaptain = hillCaptainRole && currentShift?.patrollers?.some(p => p.id === user?.id && p.role === hillCaptainRole.id);

    const loadHillCaptainData = React.useCallback(async (isRefresh = false) => {
        if (!hasHillCaptainCertification || !isAssignedAsHillCaptain) {
            // User is not assigned as Hill Captain in this shift, skip loading data
            return;
        }

        if (!isRefresh && currentShift) {
            setIsLoading(true);
        }
        
        setError(null);

        try {
            // Load all hill captain data in parallel
            const [
                reportResponse,
                equipmentResponse,
                oxygenResponse,
                tobogganResponse,
                memoResponse
            ] = await Promise.all([
                api.getReport('shift', currentShift?.id, token),
                api.getReport('equipment_checkouts', currentShift?.id, token),
                api.getEquipmentByType('oxygen', token),
                api.getEquipmentByType('toboggan', token),
                api.getReport('memos', currentShift?.id, token)
            ]);

            setReportData(reportResponse);
            setEquipmentData(equipmentResponse?.checkouts ?? []);
            setOxygenData(oxygenResponse);
            setTobogganData(tobogganResponse);
            setMemoData(memoResponse?.memos ?? []);
        } catch (err) {
            console.error('Failed to load hill captain data:', err);

            setError('Failed to load hill captain data. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [ currentShift, hasHillCaptainCertification, isAssignedAsHillCaptain, token ]);

    // Load initial data
    React.useEffect(() => {
        loadHillCaptainData();
    }, [ user?.id, currentShift?.id, loadHillCaptainData ]);

    React.useEffect(() => {
        setActiveTab(activeAlerts ? 0 : 1);
    }, [ activeAlerts ]);

    // Tab configuration
    const tabs = [
        {
            label: 'Alerts',
            icon: Notifications,
            component: Alerts,
            description: 'Notification alert events for patrollers'
        },
        {
            label: 'Weather',
            icon: WbSunny,
            component: ShiftReport,
            description: 'Weather conditions'
        },
        {
            label: 'Equipment',
            icon: Assignment,
            component: EquipmentCheckout,
            description: 'Equipment checkout and return'
        },
        {
            label: 'Oxygen',
            icon: Air,
            component: OxygenTracking,
            description: 'Oxygen tank status'
        },
        {
            label: 'Toboggans',
            icon: DownhillSkiing,
            component: TobogganStatus,
            description: 'Toboggan status and readiness'
        },
        {
            label: 'Memos',
            icon: StickyNote2,
            component: Memos,
            description: 'Communications and memo system'
        }
    ];

    const handleAlertUpdate = React.useCallback(async (key, snapshot, removal) => {
        try {
            if (removal) {
                setActiveAlerts(!!snapshot);
                setActiveTab(snapshot ? 1 : 0);
            } else {
                setActiveAlerts(!!key);
                setActiveTab(key ? 0 : 1);
            }
        } catch (error) {
            console.error('Failed to update active alerts state:', error);

            setError('Failed to update active alerts state');
        }
    }, [ ]);

    useRealtimeTrigger('manager', 'alerts',
        (key) => handleAlertUpdate(key, null, false),
        { onRemoved: (key) => handleAlertUpdate(key, null, true) }
    );

    const handleSaveShiftReport = async (reportData) => {
        try {
            const { name, shift, ...data } = reportData;
            const savedReport = await api.postReport(name, shift, data, token);

            setReportData(savedReport && reportData);

            return savedReport;
        } catch (err) {
            console.error('Failed to save report:', err);

            throw new Error('Failed to save shift report');
        }
    };

    const handleSaveEquipment = async (equipmentList) => {
        try {
            const savedReport = await api.postReport('equipment_checkouts', currentShift.id, { checkouts: equipmentList }, token);

            if (savedReport?.error) throw new Error(savedReport.error);

            setEquipmentData(equipmentList);

            return savedReport;
        } catch (err) {
            console.error('Failed to save equipment:', err);

            throw new Error('Failed to save equipment data');
        }
    };

    const handleSaveOxygen = async (oxygenList, deletedIds = []) => {
        try {
            await Promise.all(deletedIds.map(id => api.removeEquipment(id, token)));

            const savedOxygen = await api.updateEquipment(oxygenList, token);

            if (savedOxygen?.error) throw new Error(savedOxygen.error);

            const savedReport = await api.postReport('oxygen', currentShift.id, { oxygen: oxygenList, patroller: user.id }, token);

            if (savedReport?.error) throw new Error(savedReport.error);

            const errors = oxygenList.filter(oxy => {
                return savedOxygen.some(o => o.id === oxy.id && o.status === 'error');
            });

            const updated = oxygenList.map(oxy => ({
                ...oxy,
                status: errors.some(err => err.id === oxy.id) ? 'error' : oxy.status
            }));

            setOxygenData(updated);

            return errors;
        } catch (err) {
            console.error('Failed to save oxygen status:', err);

            throw err;
        }
    };

    const handleSaveToboggan = async (tobogganList, deletedIds = []) => {
        try {
            await Promise.all(deletedIds.map(id => api.removeEquipment(id, token)));

            const savedToboggan = await api.updateEquipment(tobogganList, token);

            if (savedToboggan?.error) throw new Error(savedToboggan.error);

            const savedReport = await api.postReport('toboggan', currentShift.id, { toboggans: tobogganList, patroller: user.id }, token);

            if (savedReport?.error) throw new Error(savedReport.error);

            const errors = tobogganList.filter(toboggan => {
                return savedToboggan.some(t => t.id === toboggan.id && t.status === 'error');
            });

            const updated = tobogganList.map(toboggan => ({
                ...toboggan,
                status: errors.some(err => err.id === toboggan.id) ? 'error' : toboggan.status
            }));

            setTobogganData(updated);

            return errors;
        } catch (err) {
            console.error('Failed to save toboggan status:', err);

            throw err;
        }
    };

    const handleSaveMemos = async (memoList) => {
        try {
            const savedMemos = await api.postReport('memos', currentShift.id, { memos: memoList }, token);

            if (savedMemos?.error) throw new Error(savedMemos.error);

            setMemoData(savedMemos && memoList);

            return savedMemos;
        } catch (err) {
            console.error('Failed to save memos:', err);

            throw new Error('Failed to save memo data');
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleRefresh = () => {
        loadHillCaptainData(true);
    };

    if (isLoading) {
        return (
            <div className={ styles.container }>
                <Box className={ styles.loadingBox }>
                    <Typography variant="h6">Loading Hill Captain dashboard...</Typography>
                </Box>
            </div>
        );
    }

    // if not currentShift, user doesn't have hill captain certification, or user is not assigned as hill captain in this shift, return null
    if (!currentShift || !hasHillCaptainCertification || (hasHillCaptainCertification && !isAssignedAsHillCaptain)) {
        return null;
    }

    const CurrentTabComponent = tabs[ activeTab ].component;

    return (
        <div className={ styles.container }>
            <CollapsibleCard
                title="Hill Captain Dashboard"
                headerIcon={ <WbSunny /> }
                headerColor="primary"
                initiallyExpanded={ true }
                className={ styles.hillCaptainCard }
            >
                { error && (
                    <Alert variant='filled' severity="error" className={ styles.errorAlert }>
                        { error }
                    </Alert>
                ) }

                {/* Tab Navigation */ }
                <Paper className={ styles.tabsContainer }>
                    <Tabs
                        value={ activeTab }
                        onChange={ handleTabChange }
                        variant="scrollable"
                        scrollButtons="never"
                        className={ styles.tabs }
                    >
                        { tabs.map((tab, index) => (
                            <Tab
                                key={ index }
                                label={
                                    <Box className={ styles.tabLabel }>
                                        <tab.icon 
                                            className={ styles.tabIcon } 
                                        />
                                        
                                        <span>{ tab.label }</span>
                                    </Box>
                                }
                                title={ tab.description }
                                sx={{ 
                                    color: index === 0 && activeAlerts ? 'text.primary' : index === 0 ? 'error.dark' : 'inherit',
                                    backgroundColor: index === 0 && activeAlerts ? 'error.main' : 'transparent',
                                    '&.Mui-selected': {
                                        backgroundColor: index === 0 && activeAlerts ? 'error.main' : 'transparent'
                                    }
                                }}
                            />
                        )) }
                    </Tabs>
                </Paper>

                {/* Tab Content */ }
                <Box className={ styles.tabContent }>
                    { activeTab === 0 && (
                        <CurrentTabComponent
                            patrollerId={ user?.id }
                            shiftId={ currentShift?.id }
                            currentShift={ currentShift }
                        />
                    ) }

                    { activeTab === 1 && (
                        <CurrentTabComponent
                            patrollerId={ user?.id }
                            onSave={ handleSaveShiftReport }
                            initialData={ reportData }
                        />
                    ) }

                    { activeTab === 2 && (
                        <CurrentTabComponent
                            patrollerId={ user?.id }
                            onSave={ handleSaveEquipment }
                            equipmentList={ equipmentData }
                        />
                    ) }

                    { activeTab === 3 && (
                        <CurrentTabComponent
                            patrollerId={ user?.id }
                            onSave={ handleSaveOxygen }
                            oxygenData={ oxygenData }
                        />
                    ) }

                    { activeTab === 4 && (
                        <CurrentTabComponent
                            patrollerId={ user?.id }
                            onSave={ handleSaveToboggan }
                            tobogganData={ tobogganData }
                        />
                    ) }

                    { activeTab === 5 && (
                        <CurrentTabComponent
                            patrollerId={ user?.id }
                            onSave={ handleSaveMemos }
                            memoData={ memoData }
                            shiftId={ currentShift?.id }
                        />
                    ) }
                </Box>

                { /* hiding for now */ }
                { activeTab === 100 && <Tooltip title="Refresh all data">
                    <Fab
                        color="primary"
                        className={ styles.refreshFab }
                        onClick={ handleRefresh }
                        size="medium"
                    >
                        <Sync />
                    </Fab>
                </Tooltip> }
            </CollapsibleCard>
        </div>
    );
};